import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { PURGE } from "redux-persist"
import { RootState } from "./store"

/** Dispatcher bare JID -> the chats they keep bookmarked. */
export type DispatcherChats = Record<string, string[]>

export type DispatcherChatsState = {
  byDispatcher: DispatcherChats
  status: "idle" | "loading" | "ready" | "failed"
  /** An answer has arrived this session. Until then `byDispatcher` is empty
   * for want of data, not because nobody keeps anything, so no chat may be
   * called unassigned yet. A refresh or a failed one keeps it true. */
  loaded: boolean
}

const initialState: DispatcherChatsState = {
  byDispatcher: {},
  status: "idle",
  loaded: false,
}

export const dispatcherChatsSlice = createSlice({
  name: "dispatcherChats",
  initialState,
  reducers: {
    dispatcherChatsRequested: (state) => {
      state.status = "loading"
    },
    setDispatcherChats: (state, action: PayloadAction<DispatcherChats>) => {
      // Replaced wholesale: the command answers for the whole account, so a
      // dispatcher missing from the answer no longer has any chats.
      state.byDispatcher = action.payload
      state.status = "ready"
      state.loaded = true
    },
    // The last known mapping stays, so a failed refresh does not empty a view
    // the user is looking at.
    dispatcherChatsFailed: (state) => {
      state.status = "failed"
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState)
  },
})

export const selectDispatcherChats = (state: RootState) =>
  state.dispatcherChats.byDispatcher

export const selectDispatcherChatsLoaded = (state: RootState) =>
  state.dispatcherChats.loaded

export const selectDispatcherChatsStatus = (state: RootState) =>
  state.dispatcherChats.status

export const {
  dispatcherChatsRequested,
  setDispatcherChats,
  dispatcherChatsFailed,
} = dispatcherChatsSlice.actions

export default dispatcherChatsSlice.reducer
